class AccessControls extends HTMLElement {
    constructor() {
        super();

        this.defaultSettings = {
            highContrast: false,
            reducedMotion: false,
            lineHeight: 1.5,
            textScale: 100,
        };

        this.validationLimits = {
            lineHeight: { min: 1.2, max: 3 },
            textScale: { min: 80, max: 700 },
        };

        this.settings = { ...this.defaultSettings };
        this.loadSettings();
    }

    connectedCallback() {
        this.setupListeners();
    }

    loadSettings() {
        const stored = localStorage.getItem("accessibilitySettingsV1");

        if (stored) {
            this.settings = JSON.parse(stored);
        }

        this.applySettings();
    }

    saveSettings() {
        localStorage.setItem(
            "accessibilitySettingsV1",
            JSON.stringify(this.settings),
        );
        this.applySettings();
    }

    applySettings() {
        // Apply validation limits
        const lineHeight = Math.min(
            Math.max(
                this.settings.lineHeight,
                this.validationLimits.lineHeight.min,
            ),
            this.validationLimits.lineHeight.max,
        );

        const textScale = Math.min(
            Math.max(
                this.settings.textScale,
                this.validationLimits.textScale.min,
            ),
            this.validationLimits.textScale.max,
        );

        document.documentElement.style.setProperty(
            "--base-line-height",
            lineHeight,
        );
        document.documentElement.style.fontSize = `${textScale}%`;

        document.body.classList.toggle(
            "access--line-height",
            this.settings.lineHeight !== this.defaultSettings.lineHeight,
        );

        document.body.classList.toggle(
            "access--high-contrast",
            this.settings.highContrast,
        );

        document.body.classList.toggle(
            "access--reduced-motion",
            this.settings.reducedMotion,
        );

        this.updateDisplays();
        this.updateResetButton();

        // Dispatch custom event with current settings
        const event = new CustomEvent("accessibilitySettingsChanged", {
            detail: { ...this.settings },
            bubbles: true,
        });

        this.dispatchEvent(event);
    }

    updateDisplays() {
        this.querySelectorAll("[data-display]").forEach((display) => {
            const setting = display.dataset.display;
            display.textContent =
                setting === "textScale"
                    ? `${this.settings[setting]}px`
                    : this.settings[setting];
        });
    }

    updateResetButton() {
        const resetButton = this.querySelector('[data-action="reset"]');
        const hasChanges = Object.entries(this.settings).some(
            ([key, value]) => value !== this.defaultSettings[key],
        );

        resetButton.disabled = !hasChanges;
    }

    setupListeners() {
        // Handle checkbox inputs
        this.querySelectorAll("[data-setting]").forEach((input) => {
            input.checked = this.settings[input.dataset.setting];
            input.addEventListener("change", () => {
                this.settings[input.dataset.setting] = input.checked;
                this.saveSettings();
            });
        });

        // Handle quantity inputs
        this.querySelectorAll("[data-quantity-value]").forEach((input) => {
            const target = input.dataset.target;
            input.value = this.settings[target];

            input.addEventListener("change", () => {
                let value = parseFloat(input.value);

                // Validation limits
                if (this.validationLimits[target]) {
                    value = Math.min(
                        Math.max(value, this.validationLimits[target].min),
                        this.validationLimits[target].max,
                    );
                }

                this.settings[target] = value;
                input.value = value;
                this.saveSettings();
            });
        });

        // Reset button
        this.querySelector('[data-action="reset"]').addEventListener(
            "click",
            () => {
                this.settings = { ...this.defaultSettings };

                // Update all inputs
                this.querySelectorAll("[data-setting]").forEach((input) => {
                    input.checked = this.settings[input.dataset.setting];
                });

                this.querySelectorAll("[data-quantity-value]").forEach(
                    (input) => {
                        input.value = this.settings[input.dataset.target];
                    },
                );

                this.saveSettings();
            },
        );
    }
}

customElements.define("access-controls", AccessControls);
